import { useState, useEffect, useCallback, useMemo } from "react";
import { useConfigView } from "../../Config.context";
import { usePostCustomerActivityFindFilterQuery } from "../../app/useGeneratedApi";
import { useRefreshRegister } from "../../components/RefreshProvider";
import { formatDate } from "../../utils/dateUtils";
import {
  CustomerActivityResponseView,
  CustomerActivityView,
} from "../../app/GeneratedApi";

export interface FormattedCustomerActivity {
  id: string;
  email: string;
  type: "CAMPAIGN" | "WORKFLOW" | "FORM";
  customerActivityType:
    | "OPEN"
    | "CLICK"
    | "UNSUBSCRIBE"
    | "ORDER"
    | "FORM_SUBMISSION";
  timestamp: string;
  eventTime: number;
  name: string;
}

export type ActivityType =
  | "OPEN"
  | "CLICK"
  | "UNSUBSCRIBE"
  | "ORDER"
  | "FORM_SUBMISSION";

export type ActivityFilterType = ActivityType | "ALL";

const PAGE_SIZE = 10;

export const useCustomerActivitiesPanel = ({ email }: { email?: string }) => {
  const { config } = useConfigView();
  const navigate = config?.navigateTo;

  const [tabValue, setTabValue] = useState(0);
  const [activities, setActivities] = useState<FormattedCustomerActivity[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const tabLabels: ActivityFilterType[] = useMemo(
    () =>
      config?.shopifyIntegration
        ? ["ALL", "OPEN", "CLICK", "UNSUBSCRIBE", "ORDER", "FORM_SUBMISSION"]
        : ["ALL", "OPEN", "CLICK", "UNSUBSCRIBE", "FORM_SUBMISSION"],
    [config?.shopifyIntegration]
  );

  const filterTypes = useMemo((): ActivityType[] => {
    if (tabValue === 0) {
      return tabLabels.slice(1) as ActivityType[];
    }

    return [tabLabels[tabValue] as ActivityType];
  }, [tabValue, tabLabels]);

  const filterData = useMemo(
    () => ({
      customerActivityType: filterTypes,
      email,
    }),
    [filterTypes, email]
  );

  const {
    data: activitiesData,
    isLoading,
    refetch: refetchActivities,
    isFetching,
  } = usePostCustomerActivityFindFilterQuery(
    filterData,
    {
      page,
      size: PAGE_SIZE,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  useRefreshRegister(async () => {
    setActivities([]);
    setPage(0);
    setHasMore(true);
    const result = await refetchActivities();
    if (result.data) formatActivities(result.data);
  });

  const formatActivities = (activitiesData: CustomerActivityResponseView) => {
    if (!activitiesData?.content) return;

    const formattedActivities: FormattedCustomerActivity[] =
      activitiesData.content.map((activity: CustomerActivityView) => ({
        id: activity.activityId,
        email: activity.eventEmail,
        type: activity.activityType,
        customerActivityType: activity.customerActivityType,
        timestamp: formatDate(activity.eventTime),
        eventTime: activity.eventTime,
        name: activity.name,
      }));

    setActivities((prev) =>
      page === 0 ? formattedActivities : [...prev, ...formattedActivities]
    );
    setHasMore(activitiesData.totalElements !== 0);
  };

  useEffect(() => {
    if (activitiesData) {
      formatActivities(activitiesData);
    }
  }, [activitiesData]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setActivities([]);
  }, [tabValue]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number): void => {
      setTabValue(newValue);
    },
    []
  );

  const handleActivityClick = useCallback(
    (activity: FormattedCustomerActivity): void => {
      if (!navigate) {
        console.warn("Navigate function is not available.");
        return;
      }

      const { id, type, name } = activity;
      const isTinyEmail = config?.appName === "tinyEmail";
      let url = "";

      if (isTinyEmail) {
        switch (type) {
          case "CAMPAIGN":
            url = `/main/campaigns/${id}/`;
            break;
          case "WORKFLOW":
            url = `/workflow/${id}`;
            break;
          default:
            console.warn(`Unhandled activity type for tinyEmail: ${type}`);
            return;
        }
      } else {
        switch (type) {
          case "CAMPAIGN":
            url = `/main/campaigns/overview/past-campaigns?searchTextName=${name}&page=1`;
            break;
          case "WORKFLOW":
            url = `/workflow/${id}`;
            break;
          default:
            console.warn(`Unhandled activity type for non-tinyEmail: ${type}`);
            return;
        }
      }

      console.log(`Navigating to: ${url}`);
      navigate(url);
    },
    [navigate, config?.appName]
  );

  const handleRefresh = useCallback(async (): Promise<void> => {
    setActivities([]);
    setPage(0);
    setHasMore(true);
    const result = await refetchActivities();
    if (result.data) formatActivities(result.data);
  }, []);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  return {
    tabValue,
    handleTabChange,
    activities,
    isLoading,
    isFetching,
    hasMore,
    handleRefresh,
    handleActivityClick,
    loadMore,
    tabLabels,
  };
};
